type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type SpinnerVariant = 'inline' | 'section' | 'page' | 'overlay'

interface LoadingSpinnerProps {
  size?: SpinnerSize
  variant?: SpinnerVariant
  label?: string
  className?: string
}

const DOT_SIZES: Record<SpinnerSize, { dot: number; gap: number }> = {
  xs: { dot: 5,  gap: 4  },
  sm: { dot: 6,  gap: 5  },
  md: { dot: 8,  gap: 6  },
  lg: { dot: 10, gap: 8  },
  xl: { dot: 12, gap: 10 },
}

// Three-dot wave — for inline / button loading states
function DotsSpinner({ size = 'sm' }: { size?: SpinnerSize }) {
  const { dot, gap } = DOT_SIZES[size]
  return (
    <div aria-hidden="true" style={{ display: 'flex', alignItems: 'center', gap, flexShrink: 0 }}>
      {[0, 1, 2].map(i => (
        <div
          key={i}
          style={{
            width: dot,
            height: dot,
            borderRadius: '50%',
            background: 'var(--accent)',
            willChange: 'transform, opacity',
            animation: `nudge-dot-wave 1.4s ease-in-out ${(i * 0.22).toFixed(2)}s infinite`,
          }}
        />
      ))}
    </div>
  )
}

// Full orbital animation: customers on orbit, drifting away (lapsed) and nudged back (active)
function NudgeSpinner({ darkBg = false, showLegend = false, showMessages = true, large = false }: {
  darkBg?: boolean
  showLegend?: boolean
  showMessages?: boolean
  large?: boolean
}) {
  const textColor = darkBg ? 'rgba(255,255,255,0.72)' : 'var(--text2)'
  const labelColor = darkBg ? 'rgba(255,255,255,0.55)' : 'var(--text3)'
  const sceneSize = large ? 240 : 180

  return (
    <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22 }}>
      {/* Orbital scene — larger wrapper reserves layout space when scaled */}
      <div style={{ width: sceneSize, height: sceneSize, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'relative', width: 180, height: 180, transform: large ? 'scale(1.33)' : undefined, transformOrigin: 'center', willChange: 'transform' }}>
          <div className="ns-track" />
          <div className="ns-ring" />
          <div className="ns-ring ns-ring-2" />
          <div className="ns-ring ns-ring-3" />
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} className={`ns-dot ns-dot-${i}`} />
          ))}
          <div className="ns-hub">
            {/* Brand paper plane mark */}
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </div>
        </div>
      </div>

      {/* Legend: active / lapsed */}
      {showLegend && (
        <div style={{ display: 'flex', gap: 22, alignItems: 'center' }}>
          {[{ label: 'active', color: '#0D9488' }, { label: 'lapsed', color: '#e05a2b' }].map(({ label, color }) => (
            <span
              key={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontSize: 11,
                color: labelColor,
                fontFamily: "'DM Mono', monospace",
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: color,
                  flexShrink: 0,
                  display: 'inline-block',
                  boxShadow: `0 0 0 3px ${color}1A`,
                }}
              />
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Three rotating status messages */}
      {showMessages && (
        <div style={{ position: 'relative', height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 280 }}>
          {[
            { cls: 'ns-msg-a', text: 'Keeping your customers close…' },
            { cls: 'ns-msg-b', text: 'Your next recovery is waiting…' },
            { cls: 'ns-msg-c', text: 'Stay in touch, automatically…' },
          ].map(({ cls, text }) => (
            <span
              key={cls}
              className={`ns-msg ${cls}`}
              style={{
                fontSize: 13,
                color: textColor,
                letterSpacing: '-0.005em',
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 500,
              }}
            >
              {text}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export function LoadingSpinner({
  size = 'md',
  variant = 'inline',
  label: _label,
  className = '',
}: LoadingSpinnerProps) {
  if (variant === 'inline') {
    return (
      <span
        role="status"
        aria-label="Loading"
        className={className}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <DotsSpinner size={size} />
      </span>
    )
  }

  if (variant === 'section') {
    return (
      <div
        role="status"
        aria-label="Loading"
        className={className}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}
      >
        <NudgeSpinner showLegend={false} showMessages={false} />
      </div>
    )
  }

  if (variant === 'page') {
    return (
      <div
        role="status"
        aria-label="Loading"
        className={className}
        style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: '3rem 1rem 2.5rem' }}
      >
        <NudgeSpinner showLegend showMessages large />
      </div>
    )
  }

  if (variant === 'overlay') {
    return (
      <div
        role="status"
        aria-label="Loading"
        className={className}
        style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', zIndex: 9999 }}
      >
        <NudgeSpinner darkBg showLegend={false} showMessages large />
      </div>
    )
  }

  return null
}
